import { onBeforeUnmount, resolveDynamicComponent, ref, h } from "vue";
const _registeredBase = /* @__PURE__ */ new Map();
const _registeredComponents = /* @__PURE__ */ new Map();
function useModal(base) {
  const verifyBase = () => {
    if (!_registeredBase.has(base))
      throw `No renderer available for "${base}"`;
  };
  const open = (name, props) => {
    verifyBase();
    return _registeredBase.get(base).open(name, props);
  };
  const close = (reason, all = false) => {
    verifyBase();
    if (all) {
      for (const b of _registeredBase.values()) {
        b.close(reason);
      }
    } else {
      _registeredBase.get(base).close(reason);
    }
  };
  const cancel = (reason, all = false) => {
    verifyBase();
    if (all) {
      for (const b of _registeredBase.values()) {
        b.cancel(reason);
      }
    } else {
      _registeredBase.get(base).cancel(reason);
    }
  };
  return { open, close, cancel };
}
const ModalRegist = {
  props: {
    name: {
      type: String,
      required: true
    },
    context: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props, { slots }) {
    if (_registeredComponents.has(props.name))
      throw `Modal with name "${props.name}" already exists.`;
    _registeredComponents.set(props.name, { slots, context: props.context });
    onBeforeUnmount(() => {
      _registeredComponents.delete(props.name);
    });
    return () => {
    };
  }
};
const ModalRender = {
  emits: ["opened", "closed", "canceled"],
  props: {
    name: {
      type: String,
      required: true
    },
    base: {
      type: String
    }
  },
  setup(props, { emit, slots }) {
    const resolvedComponent = resolveDynamicComponent(props.base);
    if (props.base && props.base === resolvedComponent)
      throw `ModalRender base ${props.base} is not registered. Make sure you register the component globally.`;
    if (!props.base && !slots.default)
      throw `ModalRender doesn't have a base defined nor a slot`;
    let promise = null;
    let resolve = null;
    let reject = null;
    const visibleRef = ref();
    const visibleProps = ref({});
    const open = (name, props2) => {
      if (!name || typeof name !== "string")
        throw `Modal template name must be valid. Got "${name}".`;
      if (!_registeredComponents.has(name))
        throw `Modal template ${name} not registered. Make sure that you're using <modal-regist name='${name}' /> before trying to open it.`;
      if (visibleRef.value === name && promise)
        return promise;
      else {
        if (promise)
          close({ message: "opened_other_modal" });
        emit("opened", { name });
        visibleRef.value = name;
        visibleProps.value = props2 != null ? props2 : {};
        promise = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
        return promise;
      }
    };
    const reset = () => {
      visibleRef.value = "";
      resolve = null;
      reject = null;
      promise = null;
    };
    const close = function(reason) {
      if (visibleRef.value) {
        emit("closed", { name: visibleRef.value, reason });
        resolve({ reason, context: this });
        reset();
      }
    };
    const cancel = (reason) => {
      if (visibleRef.value) {
        emit("closed", { name: visibleRef.value, reason });
        reject(reason);
        reset();
      }
    };
    if (_registeredBase.has(props.base))
      throw `There are more than one renderer for ${props.base}`;
    else
      _registeredBase.set(props.name, {
        open,
        close,
        cancel
      });
    onBeforeUnmount(() => {
      _registeredBase.delete(props.name);
    });
    return () => {
      var _a, _b;
      if (_registeredComponents.has(visibleRef.value)) {
        if (props.base) {
          const { slots: slots2, context } = _registeredComponents.get(
            visibleRef.value
          );
          return h(
            resolvedComponent,
            {
              slotProps: (_a = visibleProps.value) != null ? _a : {},
              close: close.bind(context),
              cancel: cancel.bind(context)
            },
            (_b = slots2.default) == null ? void 0 : _b.call(slots2).map((x) => {
              var _a2;
              if (typeof x.type === "string") {
                return h(x);
              } else {
                return h(x, {
                  slotProps: (_a2 = visibleProps.value) != null ? _a2 : {}
                });
              }
            })
          );
        } else {
          console.error("Not implemented");
        }
      } else
        return "";
    };
  }
};
export { ModalRegist, ModalRender, useModal };
